import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

// Import SVG icons
import EyeIcon from "../assets/icons/eye.svg";
import TrashIcon from "../assets/icons/trash.svg";

import styles from './styles/ManageSnippetsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Snippet {
  snippet_id: number;
  period_start: string;
  period_end: string;
  status: string;
  user_id: number;
  username: string;
  display_name?: string;
}

const ManageSnippetsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const navigate = useNavigate();
  const [draftSnippets, setDraftSnippets] = useState<Snippet[]>([]);
  const [sentSnippets, setSentSnippets] = useState<Snippet[]>([]);
  const [error, setError] = useState<string>("");
  const [snippetToDelete, setSnippetToDelete] = useState<Snippet | null>(null);
  const [selectedDraftSnippets, setSelectedDraftSnippets] = useState<Set<number>>(new Set());
  const [selectedSentSnippets, setSelectedSentSnippets] = useState<Set<number>>(new Set());
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Manage Snippets", "/snippets");
      hasTrackedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchSnippets = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/snippets`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch snippets.");
          return;
        }

        const data: Snippet[] = await response.json();
        const snippets = Array.isArray(data) ? data : [];
        
        // Split snippets into draft and sent
        setDraftSnippets(snippets.filter(s => s.status === "draft"));
        setSentSnippets(snippets.filter(s => s.status === "sent"));
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchSnippets();
  }, [token, selectedOrgID]);

  const handleDelete = async (snippetId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/snippets/${snippetId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete snippet.");
        return;
      }

      // Remove the deleted snippet from both arrays
      setDraftSnippets(draftSnippets.filter(s => s.snippet_id !== snippetId));
      setSentSnippets(sentSnippets.filter(s => s.snippet_id !== snippetId));
      setSnippetToDelete(null);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleBulkDelete = async (snippetIds: number[], isDraft: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/snippets/delete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          snippet_ids: Array.from(snippetIds)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete snippets.");
        return;
      }

      // Remove the deleted snippets from the appropriate array
      if (isDraft) {
        setDraftSnippets(draftSnippets.filter(s => !selectedDraftSnippets.has(s.snippet_id)));
        setSelectedDraftSnippets(new Set());
      } else {
        setSentSnippets(sentSnippets.filter(s => !selectedSentSnippets.has(s.snippet_id)));
        setSelectedSentSnippets(new Set());
      }
      setSnippetToDelete(null);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleSelectAll = (checked: boolean, isDraft: boolean) => {
    if (isDraft) {
      setSelectedDraftSnippets(checked ? new Set(draftSnippets.map(s => s.snippet_id)) : new Set());
    } else {
      setSelectedSentSnippets(checked ? new Set(sentSnippets.map(s => s.snippet_id)) : new Set());
    }
  };

  const handleSelectSnippet = (snippetId: number, checked: boolean, isDraft: boolean) => {
    if (isDraft) {
      const newSelected = new Set(selectedDraftSnippets);
      if (checked) {
        newSelected.add(snippetId);
      } else {
        newSelected.delete(snippetId);
      }
      setSelectedDraftSnippets(newSelected);
    } else {
      const newSelected = new Set(selectedSentSnippets);
      if (checked) {
        newSelected.add(snippetId);
      } else {
        newSelected.delete(snippetId);
      }
      setSelectedSentSnippets(newSelected);
    }
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  const selectedDraftCount = selectedDraftSnippets.size;
  const selectedSentCount = selectedSentSnippets.size;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Manage Snippets</h1>

      <button 
        onClick={() => navigate('/snippets/generate')}
        className={styles.generateButton}
      >
        Generate Snippets
      </button>

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          Draft Snippets ({selectedDraftCount > 0 ? `selected ${selectedDraftCount} of ${draftSnippets.length}` : draftSnippets.length})
        </h2>
        {selectedDraftCount > 0 && (
          <button 
            onClick={() => setSnippetToDelete({ status: 'draft' } as Snippet)}
            className={styles.bulkDeleteButton}
            title="Delete selected snippets"
          >
            <img src={TrashIcon} alt="Delete" className={styles.actionIcon} />
          </button>
        )}
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={`${styles.tableHeader} ${styles.checkboxColumn}`}>
              <input
                type="checkbox"
                checked={selectedDraftCount === draftSnippets.length && draftSnippets.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked, true)}
                className={styles.checkbox}
              />
            </th>
            <th className={`${styles.tableHeader} ${styles.periodColumn}`}>Period</th>
            <th className={`${styles.tableHeader} ${styles.memberColumn}`}>Member</th>
            <th className={`${styles.tableHeader} ${styles.actionsColumn}`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {draftSnippets.length > 0 ? (
            draftSnippets.map((snippet) => (
              <tr key={snippet.snippet_id}>
                <td className={`${styles.tableCell} ${styles.checkboxColumn}`}>
                  <input
                    type="checkbox"
                    checked={selectedDraftSnippets.has(snippet.snippet_id)}
                    onChange={(e) => handleSelectSnippet(snippet.snippet_id, e.target.checked, true)}
                    className={styles.checkbox}
                  />
                </td>
                <td className={`${styles.tableCell} ${styles.periodColumn}`}>
                  {`${new Date(snippet.period_start).toLocaleDateString()} - ${new Date(snippet.period_end).toLocaleDateString()}`}
                </td>
                <td className={`${styles.tableCell} ${styles.memberColumn}`}>
                  <a 
                    href={`/members/${snippet.user_id}`}
                    className={styles.memberLink}
                  >
                    {snippet.display_name 
                      ? `${snippet.display_name} (${snippet.username})`
                      : snippet.username}
                  </a>
                </td>
                <td className={`${styles.tableCell} ${styles.actionsColumn}`}>
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.iconButton}
                      onClick={() => navigate(`/members/${snippet.user_id}/snippets/${snippet.snippet_id}`)}
                      title="View"
                    >
                      <img src={EyeIcon} alt="View" className={styles.actionIcon} />
                    </button>
                    <button 
                      onClick={() => setSnippetToDelete(snippet)}
                      className={styles.iconButton}
                      title="Delete"
                    >
                      <img src={TrashIcon} alt="Delete" className={styles.actionIcon} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className={styles.tableCell}>No draft snippets found.</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          Sent Snippets ({selectedSentCount > 0 ? `selected ${selectedSentCount} of ${sentSnippets.length}` : sentSnippets.length})
        </h2>
        {selectedSentCount > 0 && (
          <button 
            onClick={() => setSnippetToDelete({ status: 'sent' } as Snippet)}
            className={styles.bulkDeleteButton}
            title="Delete selected snippets"
          >
            <img src={TrashIcon} alt="Delete" className={styles.actionIcon} />
          </button>
        )}
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={`${styles.tableHeader} ${styles.checkboxColumn}`}>
              <input
                type="checkbox"
                checked={selectedSentCount === sentSnippets.length && sentSnippets.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked, false)}
                className={styles.checkbox}
              />
            </th>
            <th className={`${styles.tableHeader} ${styles.periodColumn}`}>Period</th>
            <th className={`${styles.tableHeader} ${styles.memberColumn}`}>Member</th>
            <th className={`${styles.tableHeader} ${styles.actionsColumn}`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sentSnippets.length > 0 ? (
            sentSnippets.map((snippet) => (
              <tr key={snippet.snippet_id}>
                <td className={`${styles.tableCell} ${styles.checkboxColumn}`}>
                  <input
                    type="checkbox"
                    checked={selectedSentSnippets.has(snippet.snippet_id)}
                    onChange={(e) => handleSelectSnippet(snippet.snippet_id, e.target.checked, false)}
                    className={styles.checkbox}
                  />
                </td>
                <td className={`${styles.tableCell} ${styles.periodColumn}`}>
                  {`${new Date(snippet.period_start).toLocaleDateString()} - ${new Date(snippet.period_end).toLocaleDateString()}`}
                </td>
                <td className={`${styles.tableCell} ${styles.memberColumn}`}>
                  <a 
                    href={`/members/${snippet.user_id}`}
                    className={styles.memberLink}
                  >
                    {snippet.display_name 
                      ? `${snippet.display_name} (${snippet.username})`
                      : snippet.username}
                  </a>
                </td>
                <td className={`${styles.tableCell} ${styles.actionsColumn}`}>
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.iconButton}
                      onClick={() => navigate(`/members/${snippet.user_id}/snippets/${snippet.snippet_id}`)}
                      title="View"
                    >
                      <img src={EyeIcon} alt="View" className={styles.actionIcon} />
                    </button>
                    <button 
                      onClick={() => setSnippetToDelete(snippet)}
                      className={styles.iconButton}
                      title="Delete"
                    >
                      <img src={TrashIcon} alt="Delete" className={styles.actionIcon} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className={styles.tableCell}>No sent snippets found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {snippetToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setSnippetToDelete(null)} />
          <div className={styles.dialog}>
            <p>
              {selectedDraftCount > 0 && snippetToDelete.status === 'draft'
                ? <>Delete {selectedDraftCount} draft {selectedDraftCount === 1 ? 'snippet' : 'snippets'}?<br/><br/>This action CANNOT BE UNDONE.</>
                : selectedSentCount > 0 && snippetToDelete.status === 'sent'
                ? <>Delete {selectedSentCount} sent {selectedSentCount === 1 ? 'snippet' : 'snippets'}?<br/><br/>This action CANNOT BE UNDONE.</>
                : <>Are you sure you want to delete this snippet?<br/><br/>This action CANNOT BE UNDONE.</>}
            </p>
            <div className={styles.dialogButtons}>
              <button 
                onClick={() => {
                  if (selectedDraftCount > 0 && snippetToDelete.status === 'draft') {
                    handleBulkDelete(Array.from(selectedDraftSnippets), true);
                  } else if (selectedSentCount > 0 && snippetToDelete.status === 'sent') {
                    handleBulkDelete(Array.from(selectedSentSnippets), false);
                  } else if (snippetToDelete.snippet_id) {
                    handleDelete(snippetToDelete.snippet_id);
                  }
                }} 
                className={styles.dialogButton}
              >
                Yes, Delete
              </button>
              <button onClick={() => setSnippetToDelete(null)} className={styles.dialogCancelButton}>Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageSnippetsPage; 